import { useMutation } from "@tanstack/react-query";
import { Button } from "components";
import FileUploader from "components/fileUploader/FileUploader";
import moment from "jalali-moment";
import React, { useState } from "react";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import { useHistory } from "react-router-dom";
import { uploadExcel as upload, confirmUploadedOrders } from "Services/Orders";
import { toPersianNum } from "utils/helpers";
import { DailyOrderFileUploaderProps } from "./DailyOrderFileUploader.props";
import style from "./style.module.scss";

const selectedItemsInitialState = {
    file_name: "",
    date: "",
    vendor_id: 0,
    orders: [],
};

const DailyOrderRegistrationModal: React.FC<DailyOrderFileUploaderProps> = ({
    date,
    vendor,
    apiType,
    handleUploadConvertorExcel,
}) => {
    // --------- States ---------
    const [showModal, setShowModal] = useState(false);
    const [wrongFoodIDs, setWrongFoodIDs] = useState<{
        wrong_food_ids: string[];
        showWrongFoodIDs: boolean;
    }>({
        wrong_food_ids: [],
        showWrongFoodIDs: false,
    });
    const [checkedItems, setCheckedItems] = useState<Array<boolean>>(
        // 14 days of week (14 order)
        new Array(14).fill(false)
    );
    const [selectedItems, setSelectedItems] = useState<{
        file_name: string;
        date: string;
        vendor_id: number;
        orders: { date: string; type: number }[];
    }>(selectedItemsInitialState);

    const history = useHistory();
    // --------- API call functions ---------
    const {
        mutate: uploadExcel,
        data: uploadExcelResponse,
        isLoading,
    } = useMutation(upload, {
        onSuccess: (response) => {
            //TODO: write to enums
            if (apiType === "convert" && handleUploadConvertorExcel) {
                return handleUploadConvertorExcel(response);
            }
            // check if there is wrong_food_ids
            if (
                response.data.wrong_food_ids &&
                response.data.wrong_food_ids.length
            ) {
                setWrongFoodIDs({
                    wrong_food_ids: response.data.wrong_food_ids,
                    showWrongFoodIDs: true,
                });
            }

            // fill checkItems with false based on length of response orders
            setCheckedItems(new Array(response.data.orders.length).fill(false));

            // set file_name
            setSelectedItems({
                ...selectedItems,
                file_name: response.data.file_name,
                vendor_id: vendor.id,
                date: date,
            });
            setShowModal(true);
        },
    });

    const { mutate: confirmOrders } = useMutation(confirmUploadedOrders, {
        onSuccess: (data) => {
            if (data.data.status === "OK") redirectToNewOrderDaily();
        },
    });

    // --------- Methods ---------
    const confirmWrongFoodIDs = (value: boolean) => {
        if (value) {
            setWrongFoodIDs({
                ...wrongFoodIDs,
                showWrongFoodIDs: false,
            });
        } else {
            setShowModal(false);
            resetModalData();
        }
    };

    const resetModalData = () => {
        setCheckedItems(Array(14).fill(false));

        setSelectedItems(selectedItemsInitialState);

        setWrongFoodIDs({
            wrong_food_ids: [],
            showWrongFoodIDs: false,
        });
    };

    const checkedItemsHandler = (position: number) => {
        // find checked item
        const updatedCheckItems = checkedItems.map((item, index) =>
            index === position ? !item : item
        );

        // set state with newly checkeditems
        setCheckedItems(updatedCheckItems);

        // add checked orders to selectedItems.orders
        const selectedOrders: { date: string; type: number }[] = [];
        updatedCheckItems.forEach((item, index) => {
            if (item) {
                selectedOrders.push({
                    date: uploadExcelResponse!.data.orders[index].date,
                    type: uploadExcelResponse!.data.orders[index].type,
                });
            }
        });

        setSelectedItems({ ...selectedItems, orders: selectedOrders });
    };

    const handleUpload = (value: any) => {
        const data = new FormData();
        data.append("file", value);
        uploadExcel({
            date,
            vendorId: vendor.id,
            excelFile: data,
            apiType: apiType,
        });
    };

    const submitSelectedItems = () => {
        confirmOrders(selectedItems);
        resetModalData();
        setShowModal(false);
    };

    const redirectToNewOrderDaily = () => {
        history.push("/");
    };

    return (
        <>
            <FileUploader
                // @ts-ignore
                title="بارگذاری اکسل"
                handleFileUpload={(value: any) => {
                    handleUpload(value);
                }}
                fileExtension=".xlsx"
            />

            <Modal
                show={showModal}
                onHide={() => {
                    setShowModal(false);
                    resetModalData();
                }}
                centered
                dir="rtl"
                className="rtl"
                aria-labelledby="daily-order-modal"
                data-testid="DailyOrderModal"
            >
                <Modal.Header className="border-0" closeButton>
                    <Modal.Title
                        className={`${style.title}`}
                        id="daily-order-modal"
                    >
                        <span>اکسل وندور {vendor.title}</span>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className={`${style.container}`}>
                        {wrongFoodIDs.showWrongFoodIDs &&
                        wrongFoodIDs.wrong_food_ids ? (
                            <div>
                                <span>
                                    فود آیدی‌های زیر شناسایی نشده‌اند.
                                    <br />
                                    آیا تمایل به ادامه دارید؟
                                </span>
                                <div className="mt-3 d-flex justify-content-start align-items-center">
                                    {wrongFoodIDs.wrong_food_ids.map(
                                        (item: string, index: number) => (
                                            <span
                                                key={index}
                                                className="pl-3 pr-3"
                                            >
                                                {toPersianNum(item, true)}
                                            </span>
                                        )
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="d-flex flex-column justify-content-between align-items-around">
                                {uploadExcelResponse?.data.orders?.map(
                                    (item, index) => (
                                        <div
                                            className="d-flex justify-content-between align-items-center mb-5"
                                            key={index}
                                        >
                                            <span
                                                className={
                                                    !item.can_select
                                                        ? style.disableItem
                                                        : ""
                                                }
                                            >
                                                {moment(
                                                    item?.date,
                                                    "YYYY-MM-DD HH:mm:ss"
                                                )
                                                    .locale("fa")
                                                    .format("jYYYY/jMM/jDD")}
                                            </span>

                                            <span
                                                className={
                                                    !item.can_select
                                                        ? style.disableItem
                                                        : ""
                                                }
                                            >
                                                {item.type_str}
                                            </span>

                                            <span
                                                className={`
                                                        ${style.tag} 
                                                        ${
                                                            !item.can_select
                                                                ? ""
                                                                : "bg-transparent"
                                                        }
                                                    `}
                                            >
                                                {!item.can_select &&
                                                    item.message}
                                            </span>

                                            <Form.Check
                                                type="checkbox"
                                                className="show-vendor-checkbox"
                                                disabled={!item.can_select}
                                                onChange={() => {
                                                    checkedItemsHandler(index);
                                                }}
                                                checked={checkedItems[index]}
                                            />
                                        </div>
                                    )
                                )}
                            </div>
                        )}
                    </div>
                </Modal.Body>

                <Modal.Footer className="border-0 flex-nowrap justify-content-center">
                    {wrongFoodIDs.showWrongFoodIDs &&
                    wrongFoodIDs.wrong_food_ids ? (
                        <div className="w-100 ltr d-flex justify-content-around align-items-center">
                            <Button
                                btnText="تایید"
                                className="solid is-green w-50"
                                handleClick={() => confirmWrongFoodIDs(true)}
                            />
                            <Button
                                btnText="انصراف"
                                className="bordered is-green w-50"
                                handleClick={() => confirmWrongFoodIDs(false)}
                            />
                        </div>
                    ) : (
                        <Button
                            className={`
                                        is-green solid font-weight-normal 
                                        ${style.button} 
                                        ${isLoading ? style.checking : ""}
                                    `}
                            btnText={isLoading ? "در حال بررسی..." : "ثبت"}
                            handleClick={() => submitSelectedItems()}
                            disabled={!selectedItems.orders.length}
                        />
                    )}
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default DailyOrderRegistrationModal;
